import React, { useEffect, useRef } from 'react';
import type {
  DateSelectArg,
  DatesSetArg,
  EventApi,
  EventContentArg,
  EventInput,
  EventMountArg,
  EventSourceInput,
  FormatterInput,
} from '@fullcalendar/core';
import type { SchedulingWindowState } from 'shared/scheduling_window/utils/types';
import { useSchedulingWindowConstraints } from 'shared/scheduling_window/hooks/use_scheduling_window_constraints';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import momentPlugin from '@fullcalendar/moment';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled, { createGlobalStyle } from 'styled-components';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { bodyStyles } from '@grnhse/seedling/lib/birch/internal/internal-typography';
import type {
  CalendarEvent,
  CalendarEditMode,
  UserDateFormat,
  UserTimeFormat,
} from '../types';
import uuid from 'uuid';
import { withLocalePrefix } from '../../../shared/utils/translation';
import { FullCalendar } from 'full_calendar/full_calendar';
import DOMPurify from 'dompurify';

const t = withLocalePrefix('candidate_profile_redesign.interviews');

function InterviewerEventContent({ event }: EventContentArg) {
  return (
    <InterviewerEventContainer>
      <div
        className="fc-custom-event-title pii-blur"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(event.title),
        }}
      />
    </InterviewerEventContainer>
  );
}

const InterviewerEventContainer = styled.div`
  display: flex;
  margin-bottom: 1px;
  height: 100%;
  font-size: 12px;
  font-weight: 500;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: center;
  overflow-x: hidden;
`;

type AvailabilityCalendarProps = {
  mode: CalendarEditMode;
  timeZoneLabel: string;
  timezone?: string;
  initialEvents: Array<CalendarEvent>;
  backgroundEvents?: Array<EventInput>;
  onChange: (e: Array<CalendarEvent>) => void;
  onDateChange?: (e: DatesSetArg) => void;
  timeFormat?: UserTimeFormat;
  dateFormat?: UserDateFormat;
  schedulingWindow?: SchedulingWindowState;
  schedulingWindowIsValid?: boolean;
};

const BACKGROUND_SOURCE_ID = 'background-events';
const BLOCKED_RANGES_SOURCE_ID = 'scheduling-window-blocked';

const EMPTY: EventInput[] = [];

const AvailabilityCalendar = ({
  mode,
  timeZoneLabel,
  timezone,
  initialEvents,
  backgroundEvents = EMPTY,
  onChange,
  onDateChange,
  timeFormat = 'h:mma',
  dateFormat = 'MMM D, YYYY',
  schedulingWindow,
  schedulingWindowIsValid = true,
}: AvailabilityCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) {
      return;
    }

    const existing = api.getEventSourceById(BACKGROUND_SOURCE_ID);
    if (existing) {
      existing.remove();
    }
    api.addEventSource({
      id: BACKGROUND_SOURCE_ID,
      events: backgroundEvents,
    });
  }, [backgroundEvents]);

  /**
   * Add timezone label to title bar
   * @returns
   */
  const onCalendarLoad = () => {
    if (!timeZoneLabel) {
      return;
    }

    const existingLabel = document.querySelector('#availability_time_zone');
    if (existingLabel) {
      existingLabel.textContent = timeZoneLabel;
      return;
    }

    const newLabel = document.createElement('span');
    newLabel.setAttribute('id', 'availability_time_zone');
    newLabel.textContent = timeZoneLabel;

    const container = document.querySelector('.fc-toolbar-chunk:nth-child(2)');
    if (container) {
      container.appendChild(newLabel);
    }
    return;
  };

  const convertEventApiToCalendarEvent = (
    updatedEvents: Array<EventApi>
  ): CalendarEvent[] => {
    return updatedEvents.map((e: EventApi) => {
      return {
        allDay: e.allDay,
        start: e.startStr,
        end: e.endStr,
        greenhouse_type: e.extendedProps.greenhouse_type ?? 'candidate',
      };
    });
  };

  const handleEventsChange = (updatedEvents: Array<EventApi>) => {
    const candidateEvents = updatedEvents.filter(
      (e) =>
        e.extendedProps.greenhouse_type !== 'interviewer' &&
        e.extendedProps.greenhouse_type !== 'scheduling_window_block'
    );
    const eventValues = convertEventApiToCalendarEvent(candidateEvents);
    onChange(eventValues);
  };

  const handleDateChange = (view: DatesSetArg) => {
    if (onDateChange) {
      onDateChange(view);
    }
  };

  const handleAddEvent = (selectedEvent: DateSelectArg) => {
    const calendarApi = selectedEvent.view.calendar;

    const event: EventInput = {
      id: uuid.v4(),
      start: selectedEvent.startStr,
      end: selectedEvent.endStr,
    };
    calendarApi.unselect();
    calendarApi.addEvent(event);
  };

  const checkEventType = (
    ev: EventContentArg | EventContentArg,
    type: CalendarEvent['greenhouse_type'] | undefined
  ) => {
    return ev.event.extendedProps.greenhouse_type === type;
  };

  const isProposedTime = (ev: EventContentArg | EventContentArg) => {
    return checkEventType(ev, 'proposed');
  };

  const isInterviewerTime = (ev: EventMountArg | EventContentArg) => {
    return checkEventType(ev, 'interviewer');
  };

  const formatAs24HourTime = timeFormat === 'H:mm';

  const formattingOptions: FormatterInput = {
    hour: 'numeric',
    minute: 'numeric',
    meridiem: formatAs24HourTime ? false : 'short',
    hour12: formatAs24HourTime ? false : true,
    omitZeroMinute: !formatAs24HourTime,
  };

  const getUserTitleFormat = (format: UserDateFormat) => {
    switch (format) {
      case 'D MMM YYYY':
        return '{{D} MMM} YYYY';
      case 'MMM D, YYYY':
        return '{MMM {D}}, YYYY';
      case 'YYYY MMM D':
        return 'YYYY {MMM {D}}';
      default:
        return '{MMM {D}}, YYYY';
    }
  };

  const userDayHeaderFormat = `ddd ${dateFormat
    .replace('YYYY', '')
    .replace(',', '')}`;
  const userTitleFormat = getUserTitleFormat(dateFormat);

  const { selectAllow, eventAllow, allowableRanges } =
    useSchedulingWindowConstraints(
      schedulingWindow,
      schedulingWindowIsValid,
      timezone
    );

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) {
      return;
    }
    const existing = api.getEventSourceById(BLOCKED_RANGES_SOURCE_ID);
    if (existing) {
      existing.remove();
    }

    api.addEventSource({
      id: BLOCKED_RANGES_SOURCE_ID,
      events: allowableRanges.map(({ start, end }) => ({
        start,
        end,
        display: 'inverse-background',
        classNames: ['fc-scheduling-window-blocked'],
        removable: false,
        extendedProps: {
          greenhouse_type: 'scheduling_window_block',
          htmlTitle: t('blocked_by_scheduling_window'),
        },
      })),
    });
  }, [allowableRanges]);

  /**
   * Add remove button to event
   * @param {*} eventInfo
   */
  const addRemoveButton = (eventInfo: EventMountArg) => {
    const removeButton = document.createElement('div');
    removeButton.classList.add('remove-button');
    removeButton.title = 'Remove';

    const span = document.createElement('span');
    span.textContent = 'X';

    removeButton.append(span);
    removeButton.addEventListener('click', () => {
      eventInfo.event.remove();
    });
    eventInfo.el.prepend(removeButton);
  };

  /**
   * Adjust styling of proposed events
   * @param {*} eventInfo
   */
  const setEventAsSuggested = (eventInfo: EventMountArg) => {
    eventInfo.event.setProp('classNames', 'fc-suggested-availability-event');
  };

  const setEventAsBackground = (eventInfo: EventMountArg) => {
    eventInfo.event.setProp('display', 'background');
  };

  /**
   * Add remove button and style proposed events
   * @param {*} eventInfo
   */
  const handleEventMount = (eventInfo: EventMountArg) => {
    // For display: 'background' events, FullCalendar doesn't automatically apply
    // these styles, so manually add them here
    const borderColor = eventInfo.event.extendedProps.borderColor;
    if (borderColor) {
      eventInfo.el.style.borderColor = borderColor;
    }
    const textColor = eventInfo.event.extendedProps.textColor;
    if (textColor) {
      eventInfo.el.style.color = textColor;
    }
    const backgroundColor = eventInfo.event.extendedProps.backgroundColor;
    if (backgroundColor) {
      eventInfo.el.style.backgroundColor = backgroundColor;
    }
    const background = eventInfo.event.extendedProps.background;
    if (backgroundColor) {
      eventInfo.el.style.background = background;
    }

    // Native html tooltip
    eventInfo.el.title =
      eventInfo.event.extendedProps?.htmlTitle || eventInfo.event.title;

    if (isProposedTime(eventInfo) && mode !== 'request_availability') {
      setEventAsSuggested(eventInfo);
      setEventAsBackground(eventInfo);
      return;
    } else if (isInterviewerTime(eventInfo)) {
      setEventAsBackground(eventInfo);
      return;
    }

    if (eventInfo.event.extendedProps.removable !== false) {
      addRemoveButton(eventInfo);
    }
  };

  /**
   * For proposed events, add "suggested time" label
   * For interviewer events, render the html in the title
   */
  const renderEvent = (eventInfo: EventContentArg) => {
    if (!eventInfo?.event?.extendedProps?.greenhouse_type) {
      return true; // Render default content
    }

    if (isProposedTime(eventInfo) && mode === 'add_availability_manually') {
      return (
        <CenterText>
          <StyledSecondaryBody>{t('suggested_time')}</StyledSecondaryBody>
        </CenterText>
      );
    }

    if (isInterviewerTime(eventInfo)) {
      return <InterviewerEventContent {...eventInfo} />;
    }

    return true; // Render default content
  };

  return (
    <>
      <GlobalStyle />
      <FullCalendar
        plugins={[
          timeGridPlugin,
          interactionPlugin,
          momentTimezonePlugin,
          momentPlugin,
        ]}
        headerToolbar={{
          left: 'title',
          center: '',
          right: 'prev,next,today',
        }}
        initialView="timeGridWeek"
        views={{
          timeGrid: {
            titleFormat: userTitleFormat,
            slotLabelFormat: formattingOptions,
            dayHeaderFormat: userDayHeaderFormat,
          },
        }}
        eventTimeFormat={formattingOptions}
        allDaySlot={false}
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={false}
        weekends={true}
        ref={calendarRef}
        viewClassNames={() => {
          onCalendarLoad();
          return [];
        }}
        datesSet={handleDateChange}
        displayEventTime
        eventsSet={handleEventsChange}
        eventDidMount={handleEventMount}
        events={initialEvents as EventSourceInput}
        select={handleAddEvent}
        selectAllow={selectAllow}
        eventAllow={eventAllow}
        eventContent={renderEvent}
        timeZone={timezone}
        eventClassNames={'gh-cal-events pii-blur'}
        // Only affects the user created events; everything else should have its own override
        eventColor={FullPalette.EVERGREEN_600}
        eventBorderColor={FullPalette.EVERGREEN_600}
      />
    </>
  );
};

export default AvailabilityCalendar;

const CenterText = styled.div`
  text-align: center;
  margin-top: 2px;
`;

const StyledSecondaryBody = styled(SecondaryBody)`
  color: ${FullPalette.EVERGREEN_600};
`;

const GlobalStyle = createGlobalStyle`

  .fc-theme-standard{
    font-family: 'Untitled Sans', sans-serif;
    color: ${FullPalette.EVERGREEN_700} !important;
  }
  .fc-button {
    font-size: 1rem;
    line-height: 1.5rem;
    padding: 0.5rem 1.5rem;
    background-color: ${FullPalette.WHITE} !important;
    border: solid 1px ${FullPalette.GREEN_700} !important;
    color: ${FullPalette.GREEN_700} !important;
    font-family: 'Untitled Sans', sans-serif;
  }

  .fc-prev-button,
  .fc-next-button {
    border: none !important;
  }

  .fc-suggested-availability-event {
    border: 1px dashed ${FullPalette.EVERGREEN_E200} !important;
    background-color: ${FullPalette.EVERGREEN_E50} !important;
    color: ${FullPalette.EVERGREEN_600} !important;
    }

  .fc-active-availability-event {
    background-color: ${FullPalette.EVERGREEN_600} !important;
    border-color: ${FullPalette.EVERGREEN_600} !important;
    color: ${FullPalette.EVERGREEN_600} !important;
  }

  .fc-event-time {
    margin-left: 3px;
    text-wrap: wrap !important;
    font-size: 10px !important;
  }

  .gh-cal-events .fc-event-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 1px !important;
    line-height: 1.2;
    max-height: 1.2em;
  }

  .fc-bg-event {
    opacity: unset !important;
    border-radius: 2px !important;
  }

  .fc-toolbar-title{
    font-size: 16px !important;
    font-weight: 500;
  }

  .fc-timegrid-event-harness-inset .fc-timegrid-event{
    box-shadow: unset !important;
  }

  .fc-col-header-cell-cushion {
    font-weight: 400;
  }

  .fc-bg-event.fc-scheduling-window-blocked {
    background-image: linear-gradient(
      to bottom,
      transparent calc(100% - 1px),
      rgba(0, 0, 0, 0.06) 0
    );
    background-size: 100% calc((100% + 0.5px) / 24);
    background-repeat: repeat-y;
    background-color: ${FullPalette.GREY_E500} !important;
    border: none !important;
    border-radius: 0 !important;
    z-index: 0 !important;
  }

  .fc-bg-event:not(.fc-scheduling-window-blocked) {
    z-index: 1 !important;
  }

  .fc-today-button {
    padding-left: 16px !important;
    padding-right: 16px !important;
    border-radius: 8px !important;
    margin-left: 8px !important;
    text-transform: capitalize !important;

    &:hover:enabled {
      color: ${FullPalette.EVERGREEN_700} !important;
      border-color: ${FullPalette.EVERGREEN_700} !important;
      background-color: ${FullPalette.WHITE} !important;
    }
  }

  .remove-button{
    top: 0px;
    right: 2px;
    position: absolute;
    width: 18px;
    height: 18px;
    z-index: 1000;

    &:hover{
      background-color: ${FullPalette.IRIS_E50};
      border-radius: 15px;

      span{
        color: ${FullPalette.EVERGREEN_700} !important;
      }
    }

    span{
      cursor: pointer;
      color: ${FullPalette.WHITE};
      display: flex;
      justify-content: center;
      line-height: 18px;
    }
  }

  #availability_time_zone{
    margin-left: 0;
    ${bodyStyles};
    color: ${FullPalette.GREY_900};
    font-size: 14px;
  }
`;
